import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FirebaseContext, api } from 'common';
import { onValue, update as updateDatabase } from 'firebase/database';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import InsightsIcon from '@mui/icons-material/Insights';
import PaymentsIcon from '@mui/icons-material/Payments';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { cuisineLabel } from '../common/eatCatalog';

const defaultEatSettings = {
  enabled: true,
  currency: 'EUR',
  restaurantCommissionPercent: 10,
  eatDeliveryBaseFee: 2.9,
  eatDeliveryIncludedKm: 1,
  eatDeliveryRatePerKm: 0.85,
  eatMinDeliveryFee: 2.9,
  maxRestaurantAcceptMinutes: 12,
  eatMaxDeliveryDistanceKm: 6,
  eatMaxCourierRouteKm: 12,
  courierLocationTtlMs: 900000
};

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const money = (value) => `${Number(value || 0).toFixed(2)} EUR`;
// Branding Eats aligné VTC (or/noir) — remplace le vert MUI "success" par défaut.
const GOLD_CHIP_SX = { bgcolor: '#d4af37', color: '#000', fontWeight: 700 };
const getOrderAmount = (order = {}) => Number(order.pricing && order.pricing.total ? order.pricing.total : order.total || order.amount || 0);
const getPlatformNet = (order = {}) => Number(order.pricing && order.pricing.platformNet ? order.pricing.platformNet : 0);
const getCourierEarning = (order = {}) => Number(order.pricing && order.pricing.courierEarning ? order.pricing.courierEarning : 0);
const getRestaurantNet = (order = {}) => {
  const pricing = order.pricing || {};
  if (Number.isFinite(Number(pricing.restaurantNet))) return Number(pricing.restaurantNet);
  if (Number.isFinite(Number(pricing.restaurantSubtotal))) return Number(pricing.restaurantSubtotal);
  return (order.items || []).reduce((total, item) => total + Number(item.restaurantPrice || item.basePrice || item.originalPrice || item.price || 0) * Number(item.qty || item.quantity || 1), 0);
};
const getOrderTimestamp = (order = {}) => Number(order.deliveredAt || order.paidAt || order.updatedAt || order.createdAt || 0);
// Doit rester aligne avec ORDER_STATUS de Sourcecode/functions/eat.js
const activeOrderStatuses = ['PAYMENT_PENDING', 'PLACED', 'PAID', 'RESTAURANT_ACCEPTED', 'PREPARING', 'READY_FOR_PICKUP', 'COURIER_ASSIGNED', 'COURIER_ACCEPTED', 'PICKED_UP', 'ON_THE_WAY'];
const deliveredOrderStatuses = ['DELIVERED', 'COMPLETE', 'COMPLETED'];

const revenuePeriods = [
  { id: 'day', label: "Aujourd'hui" },
  { id: 'month', label: 'Mois' },
  { id: 'year', label: 'Année' },
  { id: 'all', label: 'Total' }
];

export default function EatAdmin() {
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const restaurants = useSelector(state => state.eatdata.restaurants || []);
  const couriers = useSelector(state => state.eatdata.couriers || []);
  const orders = useSelector(state => state.eatdata.orders || []);
  const [tab, setTab] = useState(0);
  const [eatSettings, setEatSettings] = useState(defaultEatSettings);
  const [draftSettings, setDraftSettings] = useState(defaultEatSettings);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [restaurantRevenuePeriod, setRestaurantRevenuePeriod] = useState('month');

  useEffect(() => {
    dispatch(api.fetchEatRestaurants());
    dispatch(api.fetchEatCouriers());
    dispatch(api.fetchEatOrders());
  }, [dispatch]);

  useEffect(() => {
    if (!firebase || !firebase.eatSettingsRef) return undefined;
    return onValue(firebase.eatSettingsRef, snapshot => {
      const nextSettings = { ...defaultEatSettings, ...(snapshot.val() || {}) };
      setEatSettings(nextSettings);
      setDraftSettings(nextSettings);
    });
  }, [firebase]);

  const metrics = useMemo(() => {
    const deliveredOrders = orders.filter(order => deliveredOrderStatuses.includes(String(order.status || '').toUpperCase()));
    const activeOrders = orders.filter(order => activeOrderStatuses.includes(String(order.status || '').toUpperCase()));
    const platformNet = deliveredOrders.reduce((total, order) => total + getPlatformNet(order), 0);
    const courierEarnings = deliveredOrders.reduce((total, order) => total + getCourierEarning(order), 0);
    const today = new Date();
    const inPeriod = (timestamp, period) => {
      if (period === 'all') return true;
      const date = new Date(timestamp);
      if (!timestamp || Number.isNaN(date.getTime())) return false;
      if (period === 'day') return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
      if (period === 'month') return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
      return date.getFullYear() === today.getFullYear();
    };
    const restaurantRevenue = deliveredOrders.reduce((totals, order) => {
      const amount = getRestaurantNet(order);
      const timestamp = getOrderTimestamp(order);
      revenuePeriods.forEach(period => {
        totals[period.id] = (totals[period.id] || 0) + (inPeriod(timestamp, period.id) ? amount : 0);
      });
      return totals;
    }, { day: 0, month: 0, year: 0, all: 0 });
    const restaurantRows = restaurants.map(restaurant => {
      const restaurantOrders = deliveredOrders.filter(order => (order.restaurantId || order.restaurant_id) === restaurant.id);
      const periodOrders = restaurantOrders.filter(order => inPeriod(getOrderTimestamp(order), restaurantRevenuePeriod));
      const revenue = periodOrders.reduce((total, order) => total + getRestaurantNet(order), 0);
      const commission = periodOrders.reduce((total, order) => total + getPlatformNet(order), 0);
      return {
        id: restaurant.id,
        name: restaurant.name || restaurant.id,
        city: restaurant.city || restaurant.zoneId || '-',
        active: restaurant.active !== false,
        open: Boolean(restaurant.isOpen),
        orders: periodOrders.length,
        revenue,
        commission
      };
    }).sort((left, right) => right.revenue - left.revenue || right.orders - left.orders);

    return {
      restaurants: restaurants.length,
      openRestaurants: restaurants.filter(restaurant => restaurant.active !== false && restaurant.isOpen).length,
      couriers: couriers.length,
      activeCouriers: couriers.filter(courier => courier.active).length,
      orders: orders.length,
      activeOrders: activeOrders.length,
      platformNet,
      courierEarnings,
      restaurantRevenue,
      restaurantRows
    };
  }, [couriers, orders, restaurantRevenuePeriod, restaurants]);

  const activeOrders = useMemo(() => {
    return orders
      .filter(order => activeOrderStatuses.includes(String(order.status || '').toUpperCase()))
      .sort((left, right) => Number(right.updatedAt || right.createdAt || 0) - Number(left.updatedAt || left.createdAt || 0))
      .slice(0, 10);
  }, [orders]);

  const changeSetting = (field, value, numeric = true) => {
    setDraftSettings(current => ({
      ...current,
      [field]: numeric ? numberValue(value, 0) : value
    }));
  };

  const saveSettings = async () => {
    if (!firebase || !firebase.eatSettingsRef) return;
    setSaving(true);
    setSaveMessage('');
    const patch = {
      ...draftSettings,
      restaurantCommissionPercent: Math.max(0, Math.min(50, numberValue(draftSettings.restaurantCommissionPercent, 10))),
      eatDeliveryBaseFee: Math.max(0, numberValue(draftSettings.eatDeliveryBaseFee, 2.9)),
      eatDeliveryIncludedKm: Math.max(0, numberValue(draftSettings.eatDeliveryIncludedKm, 1)),
      eatDeliveryRatePerKm: Math.max(0, numberValue(draftSettings.eatDeliveryRatePerKm, 0.85)),
      eatMinDeliveryFee: Math.max(0, numberValue(draftSettings.eatMinDeliveryFee, 2.9)),
      maxRestaurantAcceptMinutes: Math.max(1, numberValue(draftSettings.maxRestaurantAcceptMinutes, 12)),
      eatMaxDeliveryDistanceKm: Math.max(1, numberValue(draftSettings.eatMaxDeliveryDistanceKm, 6)),
      eatMaxCourierRouteKm: Math.max(1, numberValue(draftSettings.eatMaxCourierRouteKm, 12)),
      courierLocationTtlMs: Math.max(60000, numberValue(draftSettings.courierLocationTtlMs, 900000)),
      updatedAt: Date.now()
    };
    await updateDatabase(firebase.eatSettingsRef, patch);
    setSaving(false);
    setSaveMessage('Paramètres Allô Driver Eats enregistrés. Les prochaines commandes utiliseront ces règles.');
  };

  return (
    <Box sx={styles.page}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={styles.header}>
        <Box>
          <Typography variant="h4" sx={styles.title}>Console Allô Driver Eats</Typography>
          <Typography color="text.secondary">Gestion globale des commissions, restaurants, livreurs, commandes actives et règles de livraison.</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button component={Link} to="/users/5" variant="outlined">Restaurateurs</Button>
          <Button component={Link} to="/users/4" variant="outlined">Livreurs</Button>
          <Button component={Link} to="/restaurant" variant="contained">PWA restaurateur</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Metric icon={<RestaurantIcon />} label="Restaurants ouverts" value={`${metrics.openRestaurants}/${metrics.restaurants}`} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<DeliveryDiningIcon />} label="Livreurs actifs" value={`${metrics.activeCouriers}/${metrics.couriers}`} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<StorefrontIcon />} label="Commandes actives" value={`${metrics.activeOrders}/${metrics.orders}`} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<PaymentsIcon />} label="Commission produits" value={money(metrics.platformNet)} /></Grid>
      </Grid>

      <Paper sx={styles.workspace}>
        <Tabs value={tab} onChange={(event, nextTab) => setTab(nextTab)} variant="scrollable" scrollButtons="auto" sx={styles.tabs}>
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Paramètres" />
          <Tab icon={<InsightsIcon />} iconPosition="start" label="Pilotage" />
          <Tab label="Restaurants" />
          <Tab label="Livreurs" />
        </Tabs>

        {tab === 0 && (
          <Box sx={styles.panel}>
            <Stack spacing={2}>
              {saveMessage ? <Alert severity="success" onClose={() => setSaveMessage('')}>{saveMessage}</Alert> : null}
              <Paper sx={styles.configCard}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>Règles financières restaurateurs</Typography>
                    <Typography variant="body2" color="text.secondary">La commission est intégrée dans les prix produits affichés au client. Elle n'apparaît pas comme frais séparé.</Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary">Eats actif</Typography>
                    <Switch checked={draftSettings.enabled !== false} onChange={event => changeSetting('enabled', event.target.checked, false)} />
                  </Stack>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Commission restaurateur (%)" value={draftSettings.restaurantCommissionPercent} onChange={event => changeSetting('restaurantCommissionPercent', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Devise" value={draftSettings.currency || 'EUR'} onChange={event => changeSetting('currency', event.target.value, false)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Délai acceptation resto (min)" value={draftSettings.maxRestaurantAcceptMinutes} onChange={event => changeSetting('maxRestaurantAcceptMinutes', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Position livreur fraîche (ms)" value={draftSettings.courierLocationTtlMs} onChange={event => changeSetting('courierLocationTtlMs', event.target.value)} /></Grid>
                </Grid>
              </Paper>

              <Paper sx={styles.configCard}>
                <Typography sx={styles.sectionTitle}>Livraison dynamique</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Le client voit uniquement la livraison. Le gain livreur reprend ces frais de livraison.</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Base livraison" value={draftSettings.eatDeliveryBaseFee} onChange={event => changeSetting('eatDeliveryBaseFee', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Km inclus" value={draftSettings.eatDeliveryIncludedKm} onChange={event => changeSetting('eatDeliveryIncludedKm', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Prix par km sup." value={draftSettings.eatDeliveryRatePerKm} onChange={event => changeSetting('eatDeliveryRatePerKm', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Minimum livraison" value={draftSettings.eatMinDeliveryFee} onChange={event => changeSetting('eatMinDeliveryFee', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Distance client max (km)" value={draftSettings.eatMaxDeliveryDistanceKm} onChange={event => changeSetting('eatMaxDeliveryDistanceKm', event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="Route livreur max (km)" value={draftSettings.eatMaxCourierRouteKm} onChange={event => changeSetting('eatMaxCourierRouteKm', event.target.value)} /></Grid>
                </Grid>
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setDraftSettings(eatSettings)}>Annuler</Button>
                <Button variant="contained" disabled={saving} onClick={saveSettings}>{saving ? 'Enregistrement...' : 'Enregistrer la configuration Eats'}</Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={styles.panel}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}><Metric icon={<PaymentsIcon />} label="Commission Eats livrée/payée" value={money(metrics.platformNet)} /></Grid>
              <Grid item xs={12} md={3}><Metric icon={<InsightsIcon />} label="Net restaurants aujourd'hui" value={money(metrics.restaurantRevenue.day)} /></Grid>
              <Grid item xs={12} md={3}><Metric icon={<RestaurantIcon />} label="Net restaurants mois" value={money(metrics.restaurantRevenue.month)} /></Grid>
              <Grid item xs={12} md={3}><Metric icon={<DeliveryDiningIcon />} label="Gains livreurs" value={money(metrics.courierEarnings)} /></Grid>
            </Grid>
            <Paper sx={{ ...styles.configCard, mt: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography sx={styles.sectionTitle}>Revenus des restaurants</Typography>
                  <Typography variant="body2" color="text.secondary">Suivi net restaurant par période, séparé de la commission plateforme.</Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  label="Période"
                  value={restaurantRevenuePeriod}
                  onChange={event => setRestaurantRevenuePeriod(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  {revenuePeriods.map(period => <MenuItem key={period.id} value={period.id}>{period.label}</MenuItem>)}
                </TextField>
              </Stack>
              {metrics.restaurantRows.length ? metrics.restaurantRows.slice(0, 12).map(restaurant => (
                <Box key={restaurant.id} sx={styles.row}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={styles.rowTitle}>{restaurant.name}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{restaurant.city} - {restaurant.orders} commande(s) livrée(s)</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={restaurant.active ? undefined : 'default'} sx={restaurant.active ? GOLD_CHIP_SX : undefined} label={restaurant.active ? 'Actif' : 'Inactif'} />
                    <Chip size="small" color={restaurant.open ? undefined : 'warning'} sx={restaurant.open ? GOLD_CHIP_SX : undefined} label={restaurant.open ? 'Ouvert' : 'Ferme'} />
                    <Chip size="small" sx={GOLD_CHIP_SX} label={`Net resto ${money(restaurant.revenue)}`} />
                    <Chip size="small" variant="outlined" label={`Commission ${money(restaurant.commission)}`} />
                  </Stack>
                </Box>
              )) : <EmptyState title="Aucun revenu restaurant" />}
            </Paper>
            <Paper sx={{ ...styles.configCard, mt: 2 }}>
              <Typography sx={styles.sectionTitle}>Commandes actives</Typography>
              <Divider sx={{ my: 1.5 }} />
              {activeOrders.length ? activeOrders.map(order => (
                <Box key={order.id || order.reference} sx={styles.row}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={styles.rowTitle}>{order.reference || order.id}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{order.restaurantName || 'Restaurant'} - {order.customerName || 'Client'} - {order.courierName || 'Livreur à assigner'}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={order.status || 'PLACED'} />
                    <Chip size="small" sx={GOLD_CHIP_SX} label={money(getOrderAmount(order))} />
                    <Chip size="small" label={`Livraison ${money(getCourierEarning(order))}`} />
                  </Stack>
                </Box>
              )) : <EmptyState title="Aucune commande active" />}
            </Paper>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={styles.panel}>
            {restaurants.length ? restaurants.map(restaurant => (
              <Box key={restaurant.id} sx={styles.row}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box sx={{ ...styles.restaurantLogo, borderColor: restaurant.brandColor || '#C9A227' }}>
                    {restaurant.logoUrl ? <img src={restaurant.logoUrl} alt="" style={styles.restaurantLogoImg} /> : <RestaurantIcon sx={{ color: restaurant.brandColor || '#C9A227' }} />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={styles.rowTitle}>{restaurant.name || restaurant.id}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{cuisineLabel(restaurant.cuisine)} - {restaurant.specialty || restaurant.city || restaurant.zoneId || 'Zone'} - {restaurant.ownerName || restaurant.ownerUid || 'Responsable'}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {restaurant.coverImageUrl && <Chip size="small" label="Vitrine" />}
                  {restaurant.rating ? <Chip size="small" label={`Note ${Number(restaurant.rating).toFixed(1)}/5`} /> : null}
                  <Chip size="small" color={restaurant.active !== false ? undefined : 'default'} sx={restaurant.active !== false ? GOLD_CHIP_SX : undefined} label={restaurant.active !== false ? 'Actif' : 'Inactif'} />
                  <Chip size="small" color={restaurant.isOpen ? undefined : 'warning'} sx={restaurant.isOpen ? GOLD_CHIP_SX : undefined} label={restaurant.isOpen ? 'Ouvert' : 'Ferme'} />
                  <Button component={Link} to="/users/5" size="small" variant="outlined">Fiche</Button>
                </Stack>
              </Box>
            )) : <EmptyState title="Aucun restaurant" />}
          </Box>
        )}

        {tab === 3 && (
          <Box sx={styles.panel}>
            {couriers.length ? couriers.map(courier => (
              <Box key={courier.id || courier.uid} sx={styles.row}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={styles.rowTitle}>{`${courier.firstName || ''} ${courier.lastName || ''}`.trim() || courier.uid || courier.id}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{courier.mobile || 'Telephone'} - {courier.zoneId || 'Zone'} - {courier.currentOrderId || 'Libre'}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color={courier.approved ? undefined : 'warning'} sx={courier.approved ? GOLD_CHIP_SX : undefined} label={courier.approved ? 'Approuvé' : 'À valider'} />
                  {courier.rating ? <Chip size="small" label={`Note ${Number(courier.rating).toFixed(1)}/5`} /> : null}
                  <Chip size="small" color={courier.active ? undefined : 'default'} sx={courier.active ? GOLD_CHIP_SX : undefined} label={courier.active ? 'En ligne' : 'Hors ligne'} />
                  <Button component={Link} to="/users/4" size="small" variant="outlined">Fiche</Button>
                </Stack>
              </Box>
            )) : <EmptyState title="Aucun livreur Eats" />}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function Metric({ icon, label, value }) {
  return (
    <Paper sx={styles.metricCard}>
      <Box sx={styles.metricIcon}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={styles.metricValue} noWrap>{value}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
      </Box>
    </Paper>
  );
}

function EmptyState({ title }) {
  return (
    <Box sx={styles.emptyState}>
      <Typography sx={styles.rowTitle}>{title}</Typography>
    </Box>
  );
}

const styles = {
  page: {
    p: { xs: 1.5, md: 2 },
    backgroundColor: '#f6f8fb',
    minHeight: 'calc(100vh - 64px)'
  },
  header: {
    mb: 2,
    alignItems: { xs: 'stretch', md: 'center' }
  },
  title: {
    fontWeight: 900,
    color: '#172033',
    fontSize: { xs: 24, md: 32 },
    lineHeight: 1.15
  },
  metricCard: {
    p: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minHeight: 92,
    borderRadius: 2,
    boxShadow: '0 1px 6px rgba(15, 23, 42, 0.08)'
  },
  metricIcon: {
    width: 42,
    height: 42,
    borderRadius: '10px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#eef5ff',
    color: '#1769aa',
    flex: '0 0 auto'
  },
  metricValue: {
    fontWeight: 900,
    fontSize: 22,
    lineHeight: 1.1,
    color: '#172033'
  },
  workspace: {
    borderRadius: 2,
    overflow: 'hidden',
    boxShadow: '0 1px 8px rgba(15, 23, 42, 0.08)'
  },
  tabs: {
    borderBottom: '1px solid #e5e9f0',
    px: 1
  },
  panel: {
    p: { xs: 1.5, md: 2 }
  },
  configCard: {
    p: { xs: 1.5, md: 2 },
    borderRadius: 2,
    boxShadow: '0 1px 6px rgba(15, 23, 42, 0.08)'
  },
  sectionTitle: {
    fontWeight: 900,
    color: '#172033',
    fontSize: 18
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    py: 1.5,
    borderBottom: '1px solid #edf0f3',
    flexDirection: { xs: 'column', md: 'row' }
  },
  rowTitle: {
    fontWeight: 800,
    color: '#172033'
  },
  restaurantLogo: {
    width: 46,
    height: 46,
    borderRadius: 1.5,
    border: '2px solid',
    backgroundColor: '#fff',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    flex: '0 0 auto'
  },
  restaurantLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  emptyState: {
    py: 6,
    textAlign: 'center'
  }
};
